// FormTemplates.js - Complete with Proper Access Report Modal (Same as FormBuilder)

import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Card,
  Button,
  Table,
  Space,
  message,
  Popconfirm,
  Typography,
  Row,
  Col,
  Input,
  Tag,
  Modal,
  Badge,
  Select,
  Tree,
  Spin,
  Alert,
  DatePicker,
  TimePicker,
  Tooltip,
  Checkbox,
  Radio,
  Divider,
  Empty
} from "antd";
import {
  PlusOutlined,
  ReloadOutlined,
  DeleteOutlined,
  SearchOutlined,
  FileTextOutlined,
  FileAddOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CopyFilled,
  FormOutlined,
  FontSizeOutlined,
  NumberOutlined,
  CalendarOutlined,
  CheckSquareOutlined,
  BorderOutlined,
  AlignLeftOutlined,
  SwapOutlined,
  BarChartOutlined,
  CalculatorOutlined,
  EditOutlined,
  WarningOutlined,
  FieldTimeOutlined,
  ClockCircleOutlined,
  SafetyOutlined,
  CheckOutlined,
  BarChartOutlined as ReportOutlined,
  SettingOutlined,
  StarOutlined,
  ImportOutlined,
  CloseOutlined,
  EyeOutlined,
  ApiOutlined,
  UploadOutlined,
  FolderOutlined,
  MenuOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  FunctionOutlined
} from "@ant-design/icons";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import dayjs from "dayjs";
import FormAccessControl from "./FormAccessControl";

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const BREAKPOINT = 768;
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API_BASE = `${API_BASE_URL}/api`;

const COLORS = {
  primary: "#113F67",
  secondary: "#247dcaff",
  accent: "#fa8c16",
  success: "#52c41a",
  error: "#f5222d",
  warning: "#faad14",
  white: "#ffffff",
  lightBackground: "#f5f5f5",
  border: "#e9ecef",
  textSecondary: "rgba(0, 0, 0, 0.45)"
};

// Date format options
const DATE_FORMATS = [
  { value: "YYYY-MM-DD", label: "YYYY-MM-DD (2024-01-31)" },
  { value: "DD-MM-YYYY", label: "DD-MM-YYYY (31-01-2024)" },
  { value: "MM-DD-YYYY", label: "MM-DD-YYYY (01-31-2024)" },
  { value: "DD/MM/YYYY", label: "DD/MM/YYYY (31/01/2024)" },
  { value: "MM/DD/YYYY", label: "MM/DD/YYYY (01/31/2024)" },
];

// Operators for report - Same as FormBuilder
const REPORT_OPERATORS = [
  { value: 'sum', label: 'Sum (∑)', labelMr: 'बेरीज', symbol: '∑', color: '#52c41a' },
  { value: 'count', label: 'Count (#)', labelMr: 'गणना', symbol: '#', color: '#1890ff' },
  { value: 'average', label: 'Average (avg)', labelMr: 'सरासरी', symbol: 'avg', color: '#722ed1' },
  { value: 'min', label: 'Min', labelMr: 'किमान', symbol: 'Min', color: '#fa8c16' },
  { value: 'max', label: 'Max', labelMr: 'कमाल', symbol: 'Max', color: '#eb2f96' },
  { value: 'percentage', label: 'Percentage (%)', labelMr: 'टक्केवारी', symbol: '%', color: '#13c2c2' },
];

// Helper function: Get Access Summary from access_control
const getAccessSummaryFromAccessControl = (accessControl) => {
  if (!accessControl || Object.keys(accessControl).length === 0) {
    return { summary: "No Access", hasAccess: false };
  }
  
  const details = [];
  
  if (accessControl["100"] && accessControl["100"].selectedDesks?.length > 0) {
    details.push(`${accessControl["100"].selectedDesks.length} Desk(s)`);
  }
  if (accessControl["1"] && (accessControl["1"].selectedIds?.length > 0 || accessControl["1"].selectedItems?.length > 0)) {
    const count = accessControl["1"].selectedIds?.length || accessControl["1"].selectedItems?.length || 0;
    details.push(`${count} ULB(s)`);
  }
  if (accessControl["2"] && accessControl["2"].selectedIds?.length > 0) {
    details.push(`${accessControl["2"].selectedIds.length} District Admin(s)`);
  }
  if (accessControl["3"] && accessControl["3"].selectedIds?.length > 0) {
    details.push(`${accessControl["3"].selectedIds.length} Divisional Officer(s)`);
  }
  
  if (details.length === 0) {
    return { summary: "No Access", hasAccess: false };
  }
  
  return { summary: details.join(" | "), hasAccess: true };
};

// ==================== ACCESS REPORT MODAL COMPONENT (FIXED - Reads from schema fields) ====================
const AccessReportModal = ({ visible, onClose, formId, formName, language, onSaveSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fields, setFields] = useState([]);
  const [reportConfig, setReportConfig] = useState({
    reportName: '',
    reportDescription: '',
    selectedFields: [],
    operations: {},
    importantFields: []
  });
  const [expandedSections, setExpandedSections] = useState({});
  const [activeOperationField, setActiveOperationField] = useState(null);
  const [tempOperation, setTempOperation] = useState(null);

  // 🔥 FIXED: Fetch form data and read report config from schema fields
  const fetchFormData = async () => {
    if (!formId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/forms/${formId}`);
      const formData = response.data?.data;
      
      if (formData) {
        let schema = formData.schema || [];
        if (typeof schema === 'string') {
          try { schema = JSON.parse(schema); } catch(e) { schema = []; }
        }
        
        // 🔥 Read report config from schema fields (reportConfig property)
        const selectedFields = [];
        const operations = {};
        const importantFields = [];
        
        // Recursive function to collect report config from all fields
        const collectReportConfig = (field) => {
          // Check main field's reportConfig
          if (field.reportConfig && field.reportConfig.enabled === true) {
            selectedFields.push(field.id);
            if (field.reportConfig.operator) {
              operations[field.id] = { operator: field.reportConfig.operator };
            }
            if (field.reportConfig.important === true) {
              importantFields.push(field.id);
            }
          }
          
          // Also check direct report_* properties (from database)
          if (field.report_enabled === true || field.report_enabled === 1) {
            if (!selectedFields.includes(field.id)) {
              selectedFields.push(field.id);
            }
            if (field.report_operator) {
              operations[field.id] = { operator: field.report_operator };
            }
            if (field.report_important === true || field.report_important === 1) {
              if (!importantFields.includes(field.id)) {
                importantFields.push(field.id);
              }
            }
          }
          
          // Check options for radio/checkbox/dropdown
          if (field.enOptions && Array.isArray(field.enOptions)) {
            field.enOptions.forEach((option, idx) => {
              if (option.reportConfig && option.reportConfig.enabled === true) {
                const optionId = `${field.id}_opt_${idx}`;
                selectedFields.push(optionId);
                if (option.reportConfig.operator) {
                  operations[optionId] = { operator: option.reportConfig.operator };
                }
                if (option.reportConfig.important === true) {
                  importantFields.push(optionId);
                }
              }
            });
          }
          
          // Check comparison sub-fields
          if (field.subFields && Array.isArray(field.subFields)) {
            field.subFields.forEach(subField => {
              if (subField.reportConfig && subField.reportConfig.enabled === true) {
                selectedFields.push(subField.id);
                if (subField.reportConfig.operator) {
                  operations[subField.id] = { operator: subField.reportConfig.operator };
                }
                if (subField.reportConfig.important === true) {
                  importantFields.push(subField.id);
                }
              }
            });
          }
          
          // Check arithmetic fields
          if (field.arithmeticFields && Array.isArray(field.arithmeticFields)) {
            field.arithmeticFields.forEach(arithField => {
              if (arithField.reportConfig && arithField.reportConfig.enabled === true) {
                selectedFields.push(arithField.id);
                if (arithField.reportConfig.operator) {
                  operations[arithField.id] = { operator: arithField.reportConfig.operator };
                }
                if (arithField.reportConfig.important === true) {
                  importantFields.push(arithField.id);
                }
              }
            });
          }
        };
        
        // Process all fields
        schema.forEach(field => collectReportConfig(field));
        
        // Get report name/description from system_report field if exists
        let reportName = '';
        let reportDescription = '';
        const reportFieldIndex = schema.findIndex(f => f.id === 'form_report_config' || f.type === 'system_report');
        if (reportFieldIndex !== -1) {
          reportName = schema[reportFieldIndex].reportConfig?.reportName || '';
          reportDescription = schema[reportFieldIndex].reportConfig?.reportDescription || '';
        }
        
        console.log("📥 Loaded report config from schema fields:", {
          selectedFieldsCount: selectedFields.length,
          importantFieldsCount: importantFields.length,
          selectedFields: selectedFields,
          importantFields: importantFields
        });
        
        setReportConfig({
          reportName: reportName,
          reportDescription: reportDescription,
          selectedFields: selectedFields,
          operations: operations,
          importantFields: importantFields
        });
        
        setFields(schema);
      }
    } catch (error) {
      console.error("Error fetching form data:", error);
      message.error("Failed to load form configuration");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible && formId) {
      fetchFormData();
    }
  }, [visible, formId]);

  // Collect all fields with children recursively
  const collectAllFieldsWithChildren = useCallback((fieldList, parentId = null, level = 0) => {
    const result = [];
    
    for (const field of fieldList) {
      if (field.type === 'system_report') continue;
      
      result.push({ ...field, level, parentId });
      
      if (field.type === 'heading') {
        const childFields = fields.filter(f => f.parentId === field.id);
        if (childFields.length > 0) {
          result.push(...collectAllFieldsWithChildren(childFields, field.id, level + 1));
        }
      }
      
      if (field.type === 'radio') {
        const options = field.enOptions || field.options || [];
        options.forEach((option, idx) => {
          result.push({
            id: `${field.id}_opt_${idx}`,
            originalId: field.id,
            type: 'radio_option',
            enLabel: `${field.enLabel || field.label} → ${option}`,
            mrLabel: `${field.mrLabel || field.label} → ${option}`,
            isSubField: true,
            parentId: field.id,
            level: level + 1,
            optionValue: option
          });
        });
        
        const radioChildFields = fields.filter(f => f.parentId === field.id);
        if (radioChildFields.length > 0) {
          result.push(...collectAllFieldsWithChildren(radioChildFields, field.id, level + 1));
        }
      }
      
      if (field.type === 'checkbox') {
        const options = field.enOptions || field.options || [];
        options.forEach((option, idx) => {
          result.push({
            id: `${field.id}_opt_${idx}`,
            originalId: field.id,
            type: 'checkbox_option',
            enLabel: `${field.enLabel || field.label} → ${option}`,
            mrLabel: `${field.mrLabel || field.label} → ${option}`,
            isSubField: true,
            parentId: field.id,
            level: level + 1,
            optionValue: option
          });
        });
        
        const checkboxChildFields = fields.filter(f => f.parentId === field.id);
        if (checkboxChildFields.length > 0) {
          result.push(...collectAllFieldsWithChildren(checkboxChildFields, field.id, level + 1));
        }
      }
      
      if (field.type === 'dropdown') {
        const options = field.enOptions || field.options || [];
        options.forEach((option, idx) => {
          result.push({
            id: `${field.id}_opt_${idx}`,
            originalId: field.id,
            type: 'dropdown_option',
            enLabel: `${field.enLabel || field.label} → ${option}`,
            mrLabel: `${field.mrLabel || field.label} → ${option}`,
            isSubField: true,
            parentId: field.id,
            level: level + 1,
            optionValue: option
          });
        });
      }
      
      if (field.type === 'comparison' && field.subFields?.length > 0) {
        if (field.mainField) {
          result.push({
            id: `${field.id}_main`,
            originalId: field.id,
            type: 'comparison_main',
            enLabel: `${field.enLabel || field.label} (Main)`,
            mrLabel: `${field.mrLabel || field.label} (मुख्य)`,
            isSubField: true,
            parentId: field.id,
            level: level + 1
          });
        }
        field.subFields.forEach((subField, idx) => {
          result.push({
            id: subField.id,
            originalId: field.id,
            type: subField.type || 'number',
            enLabel: `${field.enLabel || field.label} - ${subField.enLabel || `Value ${idx + 1}`}`,
            mrLabel: `${field.mrLabel || field.label} - ${subField.mrLabel || `मूल्य ${idx + 1}`}`,
            isSubField: true,
            parentId: field.id,
            level: level + 1
          });
        });
      }
      
      if (field.type === 'arithmetic' && field.arithmeticFields?.length > 0) {
        field.arithmeticFields.forEach((afield, idx) => {
          result.push({
            id: afield.id,
            originalId: field.id,
            type: afield.type || 'number',
            enLabel: `${field.enLabel || field.label} - ${afield.enLabel || `Value ${idx + 1}`}`,
            mrLabel: `${field.mrLabel || field.label} - ${afield.mrLabel || `मूल्य ${idx + 1}`}`,
            isSubField: true,
            parentId: field.id,
            level: level + 1
          });
        });
        if (field.resultField) {
          result.push({
            id: `${field.id}_result`,
            originalId: field.id,
            type: 'arithmetic_result',
            enLabel: `${field.enLabel || field.label} (Result)`,
            mrLabel: `${field.mrLabel || field.label} (परिणाम)`,
            isSubField: true,
            parentId: field.id,
            level: level + 1
          });
        }
      }
      
      if (field.type === 'row' && field.children) {
        const allChildren = [...(field.children.left || []), ...(field.children.right || [])];
        if (allChildren.length > 0) {
          result.push(...collectAllFieldsWithChildren(allChildren, field.id, level + 1));
        }
      }
    }
    return result;
  }, [fields]);

  const allFieldsWithChildren = useMemo(() => {
    const mainFields = fields.filter(f => !f.parentId);
    return collectAllFieldsWithChildren(mainFields);
  }, [fields, collectAllFieldsWithChildren]);

  const parentChildMap = useMemo(() => {
    const map = {};
    allFieldsWithChildren.forEach(field => {
      if (field.parentId) {
        if (!map[field.parentId]) map[field.parentId] = [];
        map[field.parentId].push(field);
      }
    });
    return map;
  }, [allFieldsWithChildren]);

  const flattenFields = useMemo(() => {
    const flatList = [];
    const flatten = (fieldList, level = 0) => {
      fieldList.forEach((field) => {
        const parentId = field.parentId;
        if (parentId && !expandedSections[parentId] && (field.isSubField || field.type === 'radio_option' || field.type === 'checkbox_option' || field.type === 'dropdown_option')) {
          return;
        }
        
        flatList.push({
          ...field,
          level,
          srNo: flatList.length + 1,
          isSelected: reportConfig.selectedFields?.includes(field.id),
          isImportant: reportConfig.importantFields?.includes(field.id),
          currentOp: reportConfig.operations?.[field.id]?.operator,
          hasChildren: parentChildMap[field.id]?.length > 0,
          isExpanded: expandedSections[field.id] || false,
          isOperationActive: activeOperationField === field.id
        });
        
        if (parentChildMap[field.id] && expandedSections[field.id]) {
          flatten(parentChildMap[field.id], level + 1);
        }
      });
    };
    flatten(allFieldsWithChildren.filter(f => !f.parentId));
    return flatList;
  }, [allFieldsWithChildren, reportConfig, expandedSections, activeOperationField, parentChildMap]);

  const handleImportantToggle = (fieldId, checked) => {
    const newImportant = checked 
      ? [...(reportConfig.importantFields || []), fieldId]
      : (reportConfig.importantFields || []).filter(id => id !== fieldId);
    setReportConfig({ ...reportConfig, importantFields: newImportant });
  };

  const handleOperatorChange = (fieldId, operatorValue) => {
    const newOps = { ...(reportConfig.operations || {}) };
    let newSelected = [...(reportConfig.selectedFields || [])];
    
    if (operatorValue) {
      newOps[fieldId] = { operator: operatorValue };
      if (!newSelected.includes(fieldId)) newSelected.push(fieldId);
    } else {
      delete newOps[fieldId];
      newSelected = newSelected.filter(id => id !== fieldId);
    }
    
    setReportConfig({ ...reportConfig, operations: newOps, selectedFields: newSelected });
    setActiveOperationField(null);
    setTempOperation(null);
  };

  const handleClearOperator = (fieldId) => {
    handleOperatorChange(fieldId, null);
  };

  const toggleOperationPanel = (fieldId) => {
    if (activeOperationField === fieldId) {
      if (tempOperation) handleOperatorChange(fieldId, tempOperation);
      setActiveOperationField(null);
      setTempOperation(null);
    } else {
      setActiveOperationField(fieldId);
      setTempOperation(reportConfig.operations?.[fieldId]?.operator || null);
    }
  };

  const toggleSection = (fieldId) => {
    setExpandedSections(prev => ({ ...prev, [fieldId]: !prev[fieldId] }));
  };


// In AccessReportModal component - handleSave function

const handleSave = async () => {
  setSaving(true);
  try {
    // First, get the CURRENT form data from database
    const response = await axios.get(`${API_BASE}/forms/${formId}`);
    const currentForm = response.data?.data;
    if (!currentForm) throw new Error("Could not fetch current form data");
    
    let schema = currentForm.schema || [];
    if (typeof schema === 'string') {
      try { schema = JSON.parse(schema); } catch(e) { schema = []; }
    }
    
    // ========== DEBUG: Check what we have ==========
    console.log("🔍 CURRENT FORM DATA:");
    console.log("   - access_control:", JSON.stringify(currentForm.access_control, null, 2));
    console.log("   - permissions:", JSON.stringify(currentForm.permissions, null, 2));
    console.log("   - form_name:", currentForm.form_name);
    
    // Update each field's reportConfig in the schema
    const updatedSchema = schema.map(field => {
      if (reportConfig.selectedFields?.includes(field.id)) {
        return {
          ...field,
          reportConfig: {
            enabled: true,
            operator: reportConfig.operations?.[field.id]?.operator || null,
            important: reportConfig.importantFields?.includes(field.id)
          }
        };
      } else if (field.reportConfig) {
        const { reportConfig: removed, ...rest } = field;
        return rest;
      }
      return field;
    });
    
    // Update radio/checkbox/dropdown option fields
    const finalSchema = updatedSchema.map(field => {
      if (field.type === 'radio' || field.type === 'checkbox' || field.type === 'dropdown') {
        const options = field.enOptions || [];
        const updatedOptions = options.map((option, idx) => {
          const optionId = `${field.id}_opt_${idx}`;
          if (reportConfig.selectedFields?.includes(optionId)) {
            return {
              ...option,
              reportConfig: {
                enabled: true,
                operator: reportConfig.operations?.[optionId]?.operator || null,
                important: reportConfig.importantFields?.includes(optionId)
              }
            };
          } else if (option.reportConfig) {
            const { reportConfig: removed, ...rest } = option;
            return rest;
          }
          return option;
        });
        return { ...field, enOptions: updatedOptions };
      }
      
      if (field.type === 'comparison' && field.subFields) {
        const updatedSubFields = field.subFields.map((subField, idx) => {
          const subFieldId = subField.id || `${field.id}_sub_${idx}`;
          if (reportConfig.selectedFields?.includes(subFieldId)) {
            return {
              ...subField,
              reportConfig: {
                enabled: true,
                operator: reportConfig.operations?.[subFieldId]?.operator || null,
                important: reportConfig.importantFields?.includes(subFieldId)
              }
            };
          } else if (subField.reportConfig) {
            const { reportConfig: removed, ...rest } = subField;
            return rest;
          }
          return subField;
        });
        return { ...field, subFields: updatedSubFields };
      }
      
      if (field.type === 'arithmetic' && field.arithmeticFields) {
        const updatedArithFields = field.arithmeticFields.map((arithField, idx) => {
          const arithFieldId = arithField.id || `${field.id}_arith_${idx}`;
          if (reportConfig.selectedFields?.includes(arithFieldId)) {
            return {
              ...arithField,
              reportConfig: {
                enabled: true,
                operator: reportConfig.operations?.[arithFieldId]?.operator || null,
                important: reportConfig.importantFields?.includes(arithFieldId)
              }
            };
          } else if (arithField.reportConfig) {
            const { reportConfig: removed, ...rest } = arithField;
            return rest;
          }
          return arithField;
        });
        return { ...field, arithmeticFields: updatedArithFields };
      }
      
      return field;
    });
    
    // Remove any existing system_report field
    const cleanSchema = finalSchema.filter(f => f.id !== 'form_report_config' && f.type !== 'system_report');
    
    let finalCleanedSchema = cleanSchema;
    if (reportConfig.reportName || reportConfig.reportDescription) {
      finalCleanedSchema = [...cleanSchema, {
        id: 'form_report_config',
        type: 'system_report',
        reportConfig: {
          reportName: reportConfig.reportName,
          reportDescription: reportConfig.reportDescription
        },
        enLabel: 'Form Report Configuration',
        mrLabel: 'फॉर्म रिपोर्ट कॉन्फिगरेशन'
      }];
    }
    
    // ========== OPTION 1: Try PATCH request first ==========
    // Send ONLY the schema update, keep everything else as is
    try {
      // Try PATCH first (if API supports it)
      await axios.patch(`${API_BASE}/forms/${formId}`, {
        schema: finalCleanedSchema
      });
      
      console.log("✅ PATCH successful - access_control preserved!");
      message.success("Access Report Configuration saved successfully!");
      
      if (onSaveSuccess) {
        onSaveSuccess();
      }
      
      onClose();
      setSaving(false);
      return;
    } catch (patchError) {
      // If PATCH fails or not supported, use PUT with full data
      console.log("PATCH failed or not supported, using PUT...");
      
      // ========== OPTION 2: PUT with MINIMAL changes ==========
      // Extract ONLY the fields that need to be sent
      const updateData = {
        // Only send essential fields that the backend expects
        id: currentForm.id,
        form_name: currentForm.form_name,
        schema: finalCleanedSchema,
        language: currentForm.language || 'en',
        
        // CRITICAL: Explicitly preserve access_control and permissions
        access_control: currentForm.access_control,
        permissions: currentForm.permissions,
        
        // Preserve other important fields
        entry_type: currentForm.entry_type || 'single',
        form_language_mode: currentForm.form_language_mode || 'bilingual',
        status: currentForm.status !== undefined ? currentForm.status : true,
        approval_status: currentForm.approval_status || 'pending',
        workflow: currentForm.workflow || { type: "none", sequence: [] },
        sla: currentForm.sla || {},
        frequency: currentForm.frequency || { enabled: false },
        max_entries_per_user: currentForm.max_entries_per_user || 0,
        selected_ulb_classes: currentForm.selected_ulb_classes || [],
        selected_ulbs: currentForm.selected_ulbs || [],
        
        // Preserve form icon
        form_icon: currentForm.form_icon,
        form_icon_type: currentForm.form_icon_type,
        form_icon_emoji: currentForm.form_icon_emoji,
        
        // Preserve metadata
        created_by: currentForm.created_by,
        created_at: currentForm.created_at
      };
      
      // Remove undefined values
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === undefined) {
          delete updateData[key];
        }
      });
      
      console.log("📤 Sending PUT with preserved access_control:", JSON.stringify(updateData.access_control, null, 2));
      
      const putResponse = await axios.put(`${API_BASE}/forms/${formId}`, updateData);
      
      if (putResponse.data) {
        console.log("✅ PUT successful");
        message.success("Access Report Configuration saved successfully!");
        
        if (onSaveSuccess) {
          onSaveSuccess();
        }
        
        onClose();
      }
    }
  } catch (error) {
    console.error("Error saving report config:", error);
    console.error("Error response:", error.response?.data);
    message.error("Failed to save configuration: " + (error.response?.data?.error || error.message));
  } finally {
    setSaving(false);
  }
};
  const getFieldIcon = (type, isSubField = false) => {
    if (isSubField) return <NumberOutlined style={{ color: '#95a5a6', fontSize: '12px' }} />;
    switch(type) {
      case 'text': return <FontSizeOutlined />;
      case 'number': return <NumberOutlined />;
      case 'date': return <CalendarOutlined />;
      case 'checkbox': return <CheckSquareOutlined />;
      case 'radio': return <BorderOutlined />;
      case 'dropdown': return <ApiOutlined />;
      case 'heading': return <FontSizeOutlined />;
      case 'paragraph': return <AlignLeftOutlined />;
      case 'file': return <UploadOutlined />;
      case 'comparison': return <SwapOutlined />;
      case 'arithmetic': return <CalculatorOutlined />;
      case 'formula': return <FunctionOutlined />;
      default: return <FormOutlined />;
    }
  };

  const renderOperationCell = (field) => {
    const hasOperation = !!reportConfig.operations?.[field.id]?.operator;
    const currentOpData = REPORT_OPERATORS.find(op => op.value === reportConfig.operations?.[field.id]?.operator);
    const isActive = activeOperationField === field.id;
    
    return (
      <div style={{ position: 'relative' }}>
        <Checkbox
          checked={hasOperation}
          onChange={(e) => {
            if (e.target.checked) toggleOperationPanel(field.id);
            else handleClearOperator(field.id);
          }}
        >
          Add Operation
        </Checkbox>
        {hasOperation && currentOpData && (
          <Tag color={currentOpData.color} style={{ marginLeft: 8, cursor: 'pointer' }} onClick={() => toggleOperationPanel(field.id)}>
            {currentOpData.symbol} {currentOpData.label}
          </Tag>
        )}
        {isActive && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000,
            background: '#fff', border: '1px solid #d9d9d9', borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)', padding: 12, marginTop: 4, minWidth: 180
          }}>
            <div style={{ marginBottom: 8, fontSize: 12, fontWeight: 500 }}>Select Operation:</div>
            <Radio.Group value={tempOperation} onChange={(e) => setTempOperation(e.target.value)} style={{ width: '100%' }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                {REPORT_OPERATORS.map(op => (
                  <Radio key={op.value} value={op.value}>
                    <span style={{ color: op.color, fontWeight: 'bold', width: 24, display: 'inline-block' }}>{op.symbol}</span>
                    <span>{op.label}</span>
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
            <div style={{ marginTop: 12, paddingTop: 8, borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button size="small" onClick={() => handleClearOperator(field.id)}>Clear</Button>
              <Button size="small" type="primary" onClick={() => handleOperatorChange(field.id, tempOperation)} disabled={!tempOperation}>Apply</Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const selectedCount = reportConfig.selectedFields?.length || 0;
  const importantCount = reportConfig.importantFields?.length || 0;

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      width={1000}
      title={
        <Space>
          <ReportOutlined style={{ color: COLORS.secondary }} />
          <span style={{ fontSize: '18px', fontWeight: 'bold' }}>
            Access Report Configuration - {formName}
          </span>
        </Space>
      }
      footer={[
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        <Button key="save" type="primary" onClick={handleSave} loading={saving}>
          Save Configuration
        </Button>
      ]}
      centered
      maskClosable={false}
      destroyOnClose
      bodyStyle={{ padding: "20px", maxHeight: "calc(100vh - 150px)", overflowY: "auto" }}
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <Spin tip="Loading form configuration..." />
        </div>
      ) : (
        <div>
          {/* Stats */}
          <div style={{ marginBottom: 16, display: 'flex', gap: 16 }}>
            <Tag color="blue" style={{ fontSize: 12, padding: '4px 12px' }}>
              <CheckCircleOutlined /> {selectedCount} Selected
            </Tag>
            <Tag color="orange" style={{ fontSize: 12, padding: '4px 12px' }}>
              <StarOutlined /> {importantCount} Important
            </Tag>
          </div>

          {/* Excel-like Table with Expand/Collapse - Same as FormBuilder */}
          <div style={{ border: "1px solid #e0e0e0", borderRadius: "8px", overflow: "auto", maxHeight: "500px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead>
                <tr style={{ background: "#f5f5f5", borderBottom: "2px solid #e0e0e0", position: "sticky", top: 0, zIndex: 10 }}>
                  <th style={{ padding: "10px 8px", width: "40px", textAlign: "center" }}></th>
                  <th style={{ padding: "10px 12px", width: "50px" }}>#</th>
                  <th style={{ padding: "10px 12px", textAlign: "left" }}>Field Name</th>
                  <th style={{ padding: "10px 12px", width: "80px", textAlign: "center" }}>Important</th>
                  <th style={{ padding: "10px 12px", width: "160px", textAlign: "center" }}>Operation</th>
                </tr>
              </thead>
              <tbody>
                {flattenFields.length === 0 ? (
                  <tr>
                    <td colSpan="5" style={{ padding: "40px", textAlign: "center" }}>
                      <Empty description="No fields found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    </td>
                  </tr>
                ) : (
                  flattenFields.map((field) => {
                    const indentLevel = field.level;
                    const hasChildren = field.hasChildren;
                    const isExpanded = field.isExpanded;
                    const isSubField = field.isSubField === true;
                    const isRadioOption = field.type === 'radio_option';
                    const isCheckboxOption = field.type === 'checkbox_option';
                    const isDropdownOption = field.type === 'dropdown_option';
                    const subFieldPadding = (isSubField || isRadioOption || isCheckboxOption || isDropdownOption) ? 20 : 0;
                    
                    let bgColor = "transparent";
                    if (field.isSelected) bgColor = "#e6f7ff";
                    else if (field.isImportant) bgColor = "#fff7e6";
                    else if (isSubField || isRadioOption || isCheckboxOption || isDropdownOption) bgColor = "#f9f9f9";
                    
                    return (
                      <tr key={field.id} style={{ borderBottom: "1px solid #e8e8e8", background: bgColor }}>
                        {/* Expand/Collapse */}
                        <td style={{ padding: "8px 4px", textAlign: "center" }}>
                          {hasChildren && (
                            <Button type="text" size="small" onClick={() => toggleSection(field.id)} style={{ padding: 0, width: 24 }}>
                              {isExpanded ? '▼' : '▶'}
                            </Button>
                          )}
                        </td>
                        
                        {/* Sr No */}
                        <td style={{ padding: "8px 12px", textAlign: "center", color: "#8c8c8c" }}>
                          <span style={{ paddingLeft: `${indentLevel * 20 + subFieldPadding}px` }}>{field.srNo}</span>
                        </td>
                        
                        {/* Field Name */}
                        <td style={{ padding: "8px 12px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", paddingLeft: `${subFieldPadding}px` }}>
                            <span style={{ fontSize: (isSubField || isRadioOption || isCheckboxOption || isDropdownOption) ? "12px" : "14px", opacity: (isSubField || isRadioOption || isCheckboxOption || isDropdownOption) ? 0.7 : 1 }}>
                              {getFieldIcon(field.type, isSubField)}
                            </span>
                            <span style={{ fontSize: (isSubField || isRadioOption || isCheckboxOption || isDropdownOption) ? "12px" : "13px" }}>
                              {language === 'english' ? (field.enLabel || field.label) : (field.mrLabel || field.label)}
                            </span>
                            <Tag color="blue" style={{ fontSize: "10px", margin: 0 }}>
                              {field.type === 'comparison_main' ? 'Main Value' : 
                               field.type === 'arithmetic_result' ? 'Result' : 
                               field.type === 'radio_option' ? 'Radio Option' : 
                               field.type === 'checkbox_option' ? 'Checkbox Option' : 
                               field.type === 'dropdown_option' ? 'Dropdown Option' : 
                               field.type}
                            </Tag>
                            {field.arithmeticOperator && (
                              <Tag color="orange" style={{ fontSize: '9px', marginLeft: 4 }}>
                                {field.arithmeticOperator === 'add' ? '+' : 
                                 field.arithmeticOperator === 'subtract' ? '-' : 
                                 field.arithmeticOperator === 'multiply' ? '×' : 
                                 field.arithmeticOperator === 'divide' ? '÷' : 
                                 field.arithmeticOperator === 'average' ? 'avg' : '%'}
                              </Tag>
                            )}
                          </div>
                        </td>
                        
                        {/* Important Checkbox */}
                        <td style={{ padding: "8px 12px", textAlign: "center" }}>
                          <Checkbox
                            checked={field.isImportant}
                            onChange={(e) => handleImportantToggle(field.id, e.target.checked)}
                          />
                        </td>
                        
                        {/* Operation */}
                        <td style={{ padding: "8px 12px" }}>
                          {renderOperationCell(field)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Footer Summary */}
          {selectedCount > 0 && (
            <div style={{ marginTop: 16, padding: "12px", background: "#f6ffed", borderRadius: "8px" }}>
              <Space>
                <CheckCircleOutlined style={{ color: "#52c41a" }} />
                <span>{selectedCount} field(s) selected for report</span>
                {importantCount > 0 && <Tag color="orange" icon={<StarOutlined />}>{importantCount} important</Tag>}
              </Space>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

// ==================== MAIN FORM TEMPLATES COMPONENT ====================
export default function FormTemplates() {
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= BREAKPOINT);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  // State for create form modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFieldSelectionModal, setShowFieldSelectionModal] = useState(false);
  const [availableForms, setAvailableForms] = useState([]);
  const [loadingForms, setLoadingForms] = useState(false);
  const [selectedSourceForm, setSelectedSourceForm] = useState(null);
  const [sourceFormSchema, setSourceFormSchema] = useState([]);
  const [selectedFields, setSelectedFields] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [creatingFromExisting, setCreatingFromExisting] = useState(false);
  
  // State for edit confirmation modal
  const [showEditConfirmModal, setShowEditConfirmModal] = useState(false);
  const [formToEdit, setFormToEdit] = useState(null);
  
  // State for frequency modal
  const [showFrequencyModal, setShowFrequencyModal] = useState(false);
  const [selectedFormForFrequency, setSelectedFormForFrequency] = useState(null);
  const [frequencyName, setFrequencyName] = useState("");
  const [frequencyDate, setFrequencyDate] = useState(null);
  const [frequencyTime, setFrequencyTime] = useState(dayjs("09:00", "HH:mm"));
  const [frequencyFormat, setFrequencyFormat] = useState("YYYY-MM-DD");
  const [savingFrequency, setSavingFrequency] = useState(false);
  
  // ========== ACCESS CONTROL MODAL STATE ==========
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [selectedFormForAccess, setSelectedFormForAccess] = useState(null);
  const [accessPermissions, setAccessPermissions] = useState({});
  const [savingAccess, setSavingAccess] = useState(false);
  
  // ========== ACCESS REPORT MODAL STATE ==========
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedFormForReport, setSelectedFormForReport] = useState(null);
  
  const navigate = useNavigate();

  // Check if form has custom frequency
  const hasCustomFrequency = (form) => {
    if (form.frequency) {
      if (form.frequency.enabled === true && form.frequency.type === 'custom') {
        return true;
      }
    }
    
    if (form.frequency_config) {
      if (typeof form.frequency_config === 'string') {
        try {
          const parsed = JSON.parse(form.frequency_config);
          if (Array.isArray(parsed)) {
            const lastConfig = parsed[parsed.length - 1];
            if (lastConfig && lastConfig.type === 'custom') {
              return true;
            }
          } else if (parsed && parsed.type === 'custom') {
            return true;
          }
        } catch(e) {}
      } else if (Array.isArray(form.frequency_config)) {
        const lastConfig = form.frequency_config[form.frequency_config.length - 1];
        if (lastConfig && lastConfig.type === 'custom') {
          return true;
        }
      } else if (form.frequency_config.type === 'custom') {
        return true;
      }
    }
    
    return false;
  };

  // Helper function to get report config from form
// Helper function to get report config from form
const getReportConfigFromForm = (form) => {
  console.log("🔍 Getting report config for form:", form?.id, form?.form_name);
  console.log("   - schema type:", typeof form?.schema);
  console.log("   - schema is array:", Array.isArray(form?.schema));
  
  // 🔥 CRITICAL FIX: Handle schema that might be a string
  let schema = form?.schema || [];
  
  // If schema is a string, parse it
  if (typeof schema === 'string') {
    try {
      schema = JSON.parse(schema);
      console.log("   - Parsed schema string to array, length:", schema.length);
    } catch(e) {
      console.error("   - Failed to parse schema JSON:", e);
      schema = [];
    }
  }
  
  // If schema is not an array after parsing, return null
  if (!Array.isArray(schema)) {
    console.error("   - Schema is not an array! Type:", typeof schema);
    return null;
  }
  
  const selectedFields = [];
  const operations = {};
  const importantFields = [];
  
  const collectReportConfig = (field) => {
    if (!field) return;
    
    // Check field's reportConfig property
    if (field.reportConfig && field.reportConfig.enabled === true) {
      selectedFields.push(field.id);
      if (field.reportConfig.operator) {
        operations[field.id] = { operator: field.reportConfig.operator };
      }
      if (field.reportConfig.important === true) {
        importantFields.push(field.id);
      }
    }
    
    // Also check direct report_* properties (from database format)
    if (field.report_enabled === true || field.report_enabled === 1) {
      if (!selectedFields.includes(field.id)) {
        selectedFields.push(field.id);
      }
      if (field.report_operator) {
        operations[field.id] = { operator: field.report_operator };
      }
      if (field.report_important === true || field.report_important === 1) {
        if (!importantFields.includes(field.id)) {
          importantFields.push(field.id);
        }
      }
    }
    
    // Recursively check children
    if (field.children) {
      if (Array.isArray(field.children)) {
        field.children.forEach(child => collectReportConfig(child));
      } else {
        if (field.children.left) field.children.left.forEach(child => collectReportConfig(child));
        if (field.children.right) field.children.right.forEach(child => collectReportConfig(child));
      }
    }
    
    // Check subFields (comparison fields)
    if (field.subFields && Array.isArray(field.subFields)) {
      field.subFields.forEach(sub => collectReportConfig(sub));
    }
    
    // Check arithmeticFields
    if (field.arithmeticFields && Array.isArray(field.arithmeticFields)) {
      field.arithmeticFields.forEach(af => collectReportConfig(af));
    }
    
    // Check options for radio/checkbox/dropdown
    if (field.enOptions && Array.isArray(field.enOptions)) {
      field.enOptions.forEach((option, idx) => {
        if (option.reportConfig && option.reportConfig.enabled === true) {
          const optionId = `${field.id}_opt_${idx}`;
          selectedFields.push(optionId);
          if (option.reportConfig.operator) {
            operations[optionId] = { operator: option.reportConfig.operator };
          }
          if (option.reportConfig.important === true) {
            importantFields.push(optionId);
          }
        }
      });
    }
  };
  
  // Process all fields
  schema.forEach(field => collectReportConfig(field));
  
  console.log("📊 Report config result:", {
    selectedFieldsCount: selectedFields.length,
    importantFieldsCount: importantFields.length
  });
  
  if (selectedFields.length > 0) {
    return {
      reportName: '',
      reportDescription: '',
      selectedFields: selectedFields,
      operations: operations,
      importantFields: importantFields
    };
  }
  
  return null;
};
  // ========== OPEN ACCESS CONTROL MODAL ==========
  const openAccessModal = async (form) => {
    setSelectedFormForAccess(form);
    
    let existingPermissions = form.access_control || form.permissions || {};
    
    if (typeof existingPermissions === 'string') {
      try {
        existingPermissions = JSON.parse(existingPermissions);
      } catch(e) {
        existingPermissions = {};
      }
    }
    
    setAccessPermissions(existingPermissions);
    setShowAccessModal(true);
  };

  // ========== SAVE ACCESS PERMISSIONS ==========
  const handleAccessPermissionsChange = async (newPermissions) => {
    setAccessPermissions(newPermissions);
    setSavingAccess(true);
    
    try {
      const response = await axios.put(`${API_BASE}/forms/${selectedFormForAccess.id}`, {
        ...selectedFormForAccess,
        access_control: newPermissions,
        permissions: newPermissions
      });
      
      if (response.data) {
        message.success("Access permissions updated successfully");
        fetchList();
      }
    } catch (error) {
      console.error("Error saving permissions:", error);
      message.error("Failed to save access permissions");
    } finally {
      setSavingAccess(false);
    }
  };

  // ========== OPEN ACCESS REPORT MODAL ==========
  const openReportModal = (form) => {
    setSelectedFormForReport(form);
    setShowReportModal(true);
  };

  // Open frequency modal
  const openFrequencyModal = (form) => {
    setSelectedFormForFrequency(form);
    setFrequencyName("");
    setFrequencyDate(null);
    setFrequencyTime(dayjs("09:00", "HH:mm"));
    setFrequencyFormat("YYYY-MM-DD");
    setShowFrequencyModal(true);
  };

  // Save frequency settings
  const saveFrequencySettings = async () => {
    if (!frequencyName.trim()) {
      message.warning("Please enter frequency name");
      return;
    }
    
    if (!frequencyDate) {
      message.warning("Please select date");
      return;
    }
    
    setSavingFrequency(true);
    
    try {
      const currentFormResponse = await axios.get(`${API_BASE}/forms/${selectedFormForFrequency.id}`);
      const currentFullForm = currentFormResponse.data?.data;
      
      if (!currentFullForm) {
        throw new Error("Could not fetch current form data");
      }
      
      let existingSchema = currentFullForm.schema || [];
      if (typeof existingSchema === 'string') {
        try {
          existingSchema = JSON.parse(existingSchema);
        } catch (e) {
          existingSchema = [];
        }
      }
      
      const formattedDate = frequencyDate.format(frequencyFormat);
      const formattedTime = frequencyTime ? frequencyTime.format("HH:mm") : "09:00";
      
      const frequencyData = {
        enabled: true,
        type: "custom",
        custom: frequencyName,
        schedule: {
          date: formattedDate,
          time: formattedTime,
          dateFormat: frequencyFormat
        }
      };
      
      const updateData = {
        form_name: selectedFormForFrequency.form_name,
        schema: existingSchema,
        entry_type: currentFullForm.entry_type || 'single',
        form_language_mode: currentFullForm.form_language_mode || 'bilingual',
        frequency: frequencyData,
        max_entries_per_user: currentFullForm.max_entries_per_user || 0,
        permissions: currentFullForm.permissions || {},
        workflow: currentFullForm.workflow || { type: "none", sequence: [] },
        sla: currentFullForm.sla || {},
        selected_ulb_classes: currentFullForm.selected_ulb_classes || [],
        selected_ulbs: currentFullForm.selected_ulbs || [],
        form_icon: currentFullForm.form_icon,
        form_icon_type: currentFullForm.form_icon_type,
        form_icon_emoji: currentFullForm.form_icon_emoji,
        language: currentFullForm.language || 'en',
        status: currentFullForm.status !== undefined ? currentFullForm.status : true,
        approval_status: currentFullForm.approval_status || 'pending'
      };
      
      const response = await axios.put(`${API_BASE}/forms/${selectedFormForFrequency.id}`, updateData);
      
      if (response.data) {
        message.success(`Frequency "${frequencyName}" set successfully!`);
        setShowFrequencyModal(false);
        fetchList();
      }
    } catch (error) {
      console.error("Error saving frequency:", error);
      message.error("Failed to set frequency: " + (error.response?.data?.error || error.message));
    } finally {
      setSavingFrequency(false);
    }
  };

  // Window resize handler
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= BREAKPOINT);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const handleEditForm = async (form) => {
    try {
      if (form.approval_status === 'approved') {
        setFormToEdit(form);
        setShowEditConfirmModal(true);
        return;
      }
      await loadAndEditForm(form);
    } catch (error) {
      console.error("❌ Error in handleEditForm:", error);
      message.error({ content: "Failed to load form", key: 'editForm' });
    }
  };
  
  const loadAndEditForm = async (form) => {
    try {
      setLoading(true);
      message.loading({ content: 'Loading form data...', key: 'editForm' });
      
      const response = await axios.get(`${API_BASE}/forms/${form.id}`);
      const fullFormData = response.data?.data;
      
      if (fullFormData) {
        let schema = fullFormData.schema || [];
        
        if (typeof schema === 'string') {
          try {
            schema = JSON.parse(schema);
          } catch (e) {
            console.error("Schema parse error:", e);
            schema = [];
          }
        }
        
        const getFullImageUrl = (iconPath) => {
          if (!iconPath) return null;
          if (iconPath.startsWith('http') || iconPath.startsWith('data:')) {
            return iconPath;
          }
          if (iconPath.startsWith('/uploads/')) {
            const baseUrl = API_BASE_URL?.replace('/api', '') ;
            return `${baseUrl}${iconPath}`;
          }
          return iconPath;
        };
        
        let iconUrl = null;
        if (fullFormData.form_icon) {
          iconUrl = getFullImageUrl(fullFormData.form_icon);
        } else if (fullFormData.form_icon_emoji) {
          iconUrl = fullFormData.form_icon_emoji;
        }
        
        // Get report config - first check schema fields
        let reportConfigFromData = null;
        const selectedFieldsList = [];
        const operationsList = {};
        const importantFieldsList = [];
        
        const collectReportConfig = (field) => {
          if (field.reportConfig && field.reportConfig.enabled === true) {
            selectedFieldsList.push(field.id);
            if (field.reportConfig.operator) {
              operationsList[field.id] = { operator: field.reportConfig.operator };
            }
            if (field.reportConfig.important === true) {
              importantFieldsList.push(field.id);
            }
          }
        };
        
        schema.forEach(field => collectReportConfig(field));
        
        if (selectedFieldsList.length > 0) {
          reportConfigFromData = {
            selectedFields: selectedFieldsList,
            operations: operationsList,
            importantFields: importantFieldsList
          };
        }
        
        // Also check for system_report field for name/description
        const reportFieldIndex = schema.findIndex(f => f.id === 'form_report_config' || f.type === 'system_report');
        if (reportFieldIndex !== -1) {
          if (!reportConfigFromData) reportConfigFromData = {};
          reportConfigFromData.reportName = schema[reportFieldIndex].reportConfig?.reportName || '';
          reportConfigFromData.reportDescription = schema[reportFieldIndex].reportConfig?.reportDescription || '';
          schema = schema.filter((_, index) => index !== reportFieldIndex);
        }
        
        const permissionsData = fullFormData.permissions || {};
        
        let accessControlData = fullFormData.access_control || {};
        if (typeof accessControlData === 'string') {
          try {
            accessControlData = JSON.parse(accessControlData);
          } catch (e) {
            accessControlData = {};
          }
        }
        
        let frequencyData = fullFormData.frequency || { enabled: false };
        if (typeof frequencyData === 'string') {
          try {
            frequencyData = JSON.parse(frequencyData);
          } catch (e) {
            frequencyData = { enabled: false };
          }
        }
        
        let frequencyConfigData = fullFormData.frequency_config || null;
        if (typeof frequencyConfigData === 'string') {
          try {
            frequencyConfigData = JSON.parse(frequencyConfigData);
          } catch (e) {
            frequencyConfigData = null;
          }
        }
        
        let workflowData = fullFormData.workflow || { type: "none", sequence: [] };
        if (typeof workflowData === 'string') {
          try {
            workflowData = JSON.parse(workflowData);
          } catch (e) {
            workflowData = { type: "none", sequence: [] };
          }
        }
        
        let slaData = fullFormData.sla || {};
        if (typeof slaData === 'string') {
          try {
            slaData = JSON.parse(slaData);
          } catch (e) {
            slaData = {};
          }
        }
        
        let selectedUlbsData = fullFormData.selected_ulbs || [];
        if (typeof selectedUlbsData === 'string') {
          try {
            selectedUlbsData = JSON.parse(selectedUlbsData);
          } catch (e) {
            selectedUlbsData = [];
          }
        }
        
        let selectedUlbClassesData = fullFormData.selected_ulb_classes || [];
        if (typeof selectedUlbClassesData === 'string') {
          try {
            selectedUlbClassesData = JSON.parse(selectedUlbClassesData);
          } catch (e) {
            selectedUlbClassesData = [];
          }
        }
        
        const formData = {
          id: fullFormData.id,
          form_name: fullFormData.form_name || '',
          schema: schema,
          status: fullFormData.status,
          entry_type: fullFormData.entry_type || 'single',
          form_language_mode: fullFormData.form_language_mode || 'bilingual',
          approval_status: fullFormData.approval_status || 'pending',
          created_by: fullFormData.created_by,
          created_at: fullFormData.created_at,
          reviewed_by: fullFormData.reviewed_by,
          reviewed_at: fullFormData.reviewed_at,
          reviewer_notes: fullFormData.reviewer_notes,
          rejection_reason: fullFormData.rejection_reason,
          permissions: permissionsData,
          access_control: accessControlData,
          workflow: workflowData,
          sla: slaData,
          report_config: reportConfigFromData,
          selected_ulb_classes: selectedUlbClassesData,
          selected_ulbs: selectedUlbsData,
          frequency: frequencyData,
          frequency_config: frequencyConfigData,
          max_entries_per_user: fullFormData.max_entries_per_user || 0,
          form_icon: iconUrl,
          form_icon_type: fullFormData.form_icon_type || (iconUrl ? 'image' : null),
          form_icon_emoji: fullFormData.form_icon_emoji || null,
        };
        
        navigate("/super-admin/form-templates/form-builder", {
          state: {
            mode: "edit",
            formId: form.id,
            formData: formData,
            isEditing: true,
            wasApproved: fullFormData.approval_status === 'approved',
            isEditingApproved: fullFormData.approval_status === 'approved',
            permissions: permissionsData,
            report_config: reportConfigFromData,
            access_control: accessControlData,
            frequency: frequencyData,
            frequency_config: frequencyConfigData,
            workflow: workflowData,
            sla: slaData,
            selected_ulb_classes: selectedUlbClassesData,
            selected_ulbs: selectedUlbsData
          }
        });
      }
    } catch (error) {
      console.error("❌ Error loading form:", error);
      message.error({ content: "Failed to load form", key: 'editForm' });
      throw error;
    } finally {
      setLoading(false);
    }
  };
  
  const handleConfirmEditApproved = async () => {
    setShowEditConfirmModal(false);
    if (formToEdit) {
      await loadAndEditForm(formToEdit);
    }
  };

  const fetchList = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/forms`);
      const forms = Array.isArray(res.data?.data) ? res.data.data : [];
      
      const sortedForms = [...forms].sort((a, b) => b.id - a.id);
      
      const formsWithMeta = sortedForms.map((form, index) => ({
        ...form,
        serialNo: index + 1,
        status: form.status !== undefined ? form.status : true
      }));
      
      setRows(formsWithMeta);
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      setFilteredRows(formsWithMeta.slice(startIndex, endIndex));
    } catch (e) {
      console.error("Fetch error:", e);
      message.error("Failed to load forms list");
    } finally {
      setLoading(false);
    }
  };

  const setFilteredFormsByPage = (allForms, page, size) => {
    const startIndex = (page - 1) * size;
    const endIndex = startIndex + size;
    setFilteredRows(allForms.slice(startIndex, endIndex));
  };

  const fetchAvailableForms = async () => {
    try {
      setLoadingForms(true);
      const res = await axios.get(`${API_BASE}/forms`);
      setAvailableForms(res.data?.data || []);
    } catch (error) {
      console.error("Fetch available forms error:", error);
      message.error("Failed to load forms");
    } finally {
      setLoadingForms(false);
    }
  };

  const loadSourceFormSchema = async (formId) => {
    try {
      setLoadingForms(true);
      const res = await axios.get(`${API_BASE}/forms/${formId}`);
      const formData = res.data?.data;
      
      if (formData) {
        setSelectedSourceForm({
          id: formData.id,
          name: formData.form_name
        });
        
        let schema = formData.schema || [];
        if (typeof schema === 'string') {
          try {
            schema = JSON.parse(schema);
          } catch (e) {
            schema = [];
          }
        }
        
        setSourceFormSchema(schema);
        
        const allIds = [];
        const collectIds = (nodes) => {
          nodes.forEach(node => {
            allIds.push(node.id);
            if (node.children) {
              if (Array.isArray(node.children)) {
                collectIds(node.children);
              } else {
                if (node.children.left) collectIds(node.children.left);
                if (node.children.right) collectIds(node.children.right);
              }
            }
            if (node.subFields) {
              collectIds(node.subFields);
            }
          });
        };
        collectIds(schema);
        setExpandedKeys(allIds);
      }
    } catch (error) {
      console.error("Load schema error:", error);
      message.error("Failed to load form schema");
    } finally {
      setLoadingForms(false);
    }
  };

  const deleteForm = async (id) => {
    try {
      setDeletingId(id);
      await axios.delete(`${API_BASE}/forms/${id}`);
      message.success("Deleted successfully");
      fetchList();
    } catch (e) {
      console.error("Delete error:", e);
      message.error("Delete failed: " + (e.response?.data?.error || e.message));
    } finally {
      setDeletingId(null);
    }
  };

  const toggleFormStatus = async (id, currentStatus) => {
    try {
      setUpdatingId(id);
      const newStatus = !currentStatus;
      
      let response;
      try {
        response = await axios.put(`${API_BASE}/forms/${id}/status`, { status: newStatus });
      } catch (error) {
        if (error.response?.status === 404) {
          response = await axios.put(`${API_BASE}/forms/${id}`, { status: newStatus });
        } else {
          throw error;
        }
      }
      
      message.success(`Form ${newStatus ? 'activated' : 'inactivated'} successfully`);
      
      setRows(prevRows => 
        prevRows.map(row => 
          row.id === id ? { ...row, status: newStatus } : row
        )
      );
      setFilteredRows(prevRows => 
        prevRows.map(row => 
          row.id === id ? { ...row, status: newStatus } : row
        )
      );
      
      fetchList();
    } catch (e) {
      console.error("Status update error:", e);
      message.error("Failed to update form status: " + (e.response?.data?.error || e.message));
    } finally {
      setUpdatingId(null);
    }
  };

  const handleCreateNewForm = () => {
    setShowCreateModal(true);
    fetchAvailableForms();
  };

  const handleCreateBlankForm = () => {
    setShowCreateModal(false);
    navigate("/super-admin/form-templates/form-builder", {
      state: {
        mode: "new",
        formData: {
          formName: "Untitled Form",
          district: "",
          department: "",
          description: ""
        }
      }
    });
  };

  const createFormFromExisting = async () => {
    if (!selectedSourceForm) {
      message.warning("Please select a source form");
      return;
    }

    if (selectedFields.length === 0) {
      message.warning("Please select at least one field to include");
      return;
    }

    try {
      setCreatingFromExisting(true);

      const selectedFieldObjects = [];
      
      const collectSelectedFields = (nodes) => {
        nodes.forEach(node => {
          if (selectedFields.includes(node.id)) {
            const cleanField = {
              id: node.id,
              type: node.type,
              enLabel: node.enLabel || node.label,
              mrLabel: node.mrLabel || node.label,
              enPlaceholder: node.enPlaceholder || node.placeholder || '',
              mrPlaceholder: node.mrPlaceholder || node.placeholder || '',
              enRequired: node.enRequired || node.required || false,
              mrRequired: node.mrRequired || false,
              enOptions: node.enOptions || node.options || [],
              mrOptions: node.mrOptions || [],
              enHelpText: node.enHelpText || node.helpText || '',
              mrHelpText: node.mrHelpText || '',
              validation: node.validation || {},
              hasUnit: node.hasUnit || false,
              unitOptions: node.unitOptions || [],
              reportConfig: node.reportConfig || null,
              position: node.position || 0,
              parentId: node.parentId || null,
              colName: node.colName || null
            };
            
            if (node.type === 'radio') {
              cleanField.isRadioGroup = true;
              cleanField.radioOptions = cleanField.enOptions;
            }
            if (node.type === 'checkbox') {
              cleanField.isCheckboxGroup = true;
              cleanField.checkboxOptions = cleanField.enOptions;
            }
            if (node.type === 'comparison' && node.mainField) {
              cleanField.mainField = node.mainField;
              cleanField.comparisonOperator = node.comparisonOperator || 'equals';
              cleanField.subFields = node.subFields || [];
            }
            if (node.type === 'arithmetic') {
              cleanField.isArithmeticGroup = true;
              cleanField.arithmeticOperator = node.arithmeticOperator || 'add';
              cleanField.arithmeticFields = node.arithmeticFields || [];
              cleanField.showResultBox = node.showResultBox !== false;
              cleanField.resultField = node.resultField || null;
            }
            
            selectedFieldObjects.push(cleanField);
          }
          
          if (node.children) {
            if (Array.isArray(node.children)) {
              collectSelectedFields(node.children);
            } else {
              if (node.children.left) collectSelectedFields(node.children.left);
              if (node.children.right) collectSelectedFields(node.children.right);
            }
          }
          if (node.subFields) {
            collectSelectedFields(node.subFields);
          }
        });
      };
      
      collectSelectedFields(sourceFormSchema);

      const newFormName = prompt(
        `Enter name for new form (based on "${selectedSourceForm.name}"):`,
        `${selectedSourceForm.name} (Copy)`
      );

      if (!newFormName) {
        setCreatingFromExisting(false);
        return;
      }

      const response = await axios.post(`${API_BASE}/forms/from-existing`, {
        form_name: newFormName,
        linked_form_id: selectedSourceForm.id,
        selected_fields: selectedFields,
        schema: selectedFieldObjects,
        entry_type: 'single',
        form_language_mode: 'bilingual'
      });

      if (response.data?.data) {
        message.success(`Form "${newFormName}" created successfully!`);
        
        setShowFieldSelectionModal(false);
        setShowCreateModal(false);
        setSelectedSourceForm(null);
        setSourceFormSchema([]);
        setSelectedFields([]);
        
        fetchList();
        
        if (response.data.data.id) {
          navigate("/super-admin/form-templates/form-builder", {
            state: {
              mode: "edit",
              formId: response.data.data.id,
              formData: response.data.data
            }
          });
        }
      }
    } catch (error) {
      console.error("Error creating form from existing:", error);
      message.error("Failed to create form: " + (error.response?.data?.error || error.message));
    } finally {
      setCreatingFromExisting(false);
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    if (!value.trim()) {
      setFilteredFormsByPage(rows, currentPage, pageSize);
      return;
    }
    
    const filtered = rows.filter(row =>
      row.form_name?.toLowerCase().includes(value.toLowerCase()) ||
      row.id?.toString().includes(value)
    );
    setFilteredRows(filtered);
    setCurrentPage(1);
  };

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
    setFilteredFormsByPage(rows, pagination.current, pagination.pageSize);
  };

  useEffect(() => {
    fetchList();
  }, []);

  const getApprovalStatusBadge = (status) => {
    switch(status) {
      case 'approved':
        return <Tag color="success" icon={<CheckCircleOutlined />} style={{ borderRadius: 12 }}>Approved</Tag>;
      case 'rejected':
        return <Tag color="error" icon={<CloseCircleOutlined />} style={{ borderRadius: 12 }}>Rejected</Tag>;
      case 'pending':
        return <Tag color="warning" icon={<WarningOutlined />} style={{ borderRadius: 12 }}>Pending</Tag>;
      default:
        return <Tag color="default">Draft</Tag>;
    }
  };

  const columns = [
    { 
      title: "Sr. No", 
      key: "serialNo",
      width: 70,
      render: (_, __, index) => {
        const serialNumber = (currentPage - 1) * pageSize + index + 1;
        return (
          <span style={{ color: COLORS.textSecondary, fontWeight: 500 }}>
            {serialNumber}
          </span>
        );
      }
    },
    { 
      title: "Form Name", 
      dataIndex: "form_name", 
      key: "form_name",
      render: (text, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontWeight: 500 }}>{text}</span>
          {record.status ? (
            <Tag color="success" style={{ borderRadius: 12 }}>Active</Tag>
          ) : (
            <Tag color="error" style={{ borderRadius: 12 }}>Inactive</Tag>
          )}
          {getApprovalStatusBadge(record.approval_status)}
        </div>
      )
    },
    // ✅ ACCESS COLUMN (Access Control)
    {
      title: "Access Control",
      key: "access",
      width: 180,
      render: (_, record) => {
        const accessData = record.access_control || record.permissions || {};
        const accessInfo = getAccessSummaryFromAccessControl(accessData);
        
        return (
          <Tooltip title="Click to configure access permissions" placement="topLeft">
            <Button
              type={accessInfo.hasAccess ? "default" : "dashed"}
              size="small"
              icon={<SafetyOutlined />}
              onClick={() => openAccessModal(record)}
              style={{
                borderColor: accessInfo.hasAccess ? COLORS.success : undefined,
                color: accessInfo.hasAccess ? COLORS.success : undefined,
                width: '100%'
              }}
            >
              {accessInfo.summary}
            </Button>
          </Tooltip>
        );
      }
    },
    // ✅ ACCESS REPORT COLUMN
    {
      title: <span><ReportOutlined /> Access Report</span>,
      key: "report",
      width: 180,
      render: (_, record) => {
        const reportConfig = getReportConfigFromForm(record);
        const hasReportConfig = reportConfig && reportConfig.selectedFields && reportConfig.selectedFields.length > 0;
        const selectedCount = hasReportConfig ? reportConfig.selectedFields.length : 0;
        
        return (
          <Tooltip title="Configure Access Report Settings" placement="topLeft">
            <Button
              type={hasReportConfig ? "primary" : "default"}
              size="small"
              icon={<ReportOutlined />}
              onClick={() => openReportModal(record)}
              style={{
                backgroundColor: hasReportConfig ? COLORS.success : undefined,
                borderColor: hasReportConfig ? COLORS.success : undefined,
                color: hasReportConfig ? COLORS.white : COLORS.secondary,
                width: '100%'
              }}
            >
              {hasReportConfig ? `Configured (${selectedCount})` : "Configure Report"}
            </Button>
          </Tooltip>
        );
      }
    },
    {
      title: "Active Status",
      key: "status",
      width: 120,
      render: (_, record) => (
        <Popconfirm
          title={`${record.status ? 'Inactivate' : 'Activate'} Form`}
          description={`Are you sure you want to ${record.status ? 'inactivate' : 'activate'} "${record.form_name}"?`}
          onConfirm={() => toggleFormStatus(record.id, record.status)}
          okText="Yes"
          cancelText="No"
          okButtonProps={{ 
            style: { 
              backgroundColor: record.status ? COLORS.error : COLORS.success,
              borderColor: record.status ? COLORS.error : COLORS.success 
            } 
          }}
        >
          <Button
            type={record.status ? "default" : "primary"}
            size="small"
            loading={updatingId === record.id}
            style={{ 
              backgroundColor: record.status ? COLORS.white : COLORS.success,
              borderColor: record.status ? COLORS.error : COLORS.success,
              color: record.status ? COLORS.error : COLORS.white,
              minWidth: '70px'
            }}
          >
            {record.status ? 'Inactivate' : 'Activate'}
          </Button>
        </Popconfirm>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 280,
      render: (_, record) => {
        const showFrequencyBtn = hasCustomFrequency(record);
        
        return (
          <Space size="small" wrap>
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEditForm(record)}
              style={{ 
                backgroundColor: record.approval_status === 'approved' ? COLORS.warning : COLORS.primary,
                borderColor: record.approval_status === 'approved' ? COLORS.warning : COLORS.primary
              }}
            >
              Edit
            </Button>
            
            {showFrequencyBtn && (
              <Button
                type="default"
                icon={<FieldTimeOutlined />}
                size="small"
                onClick={() => openFrequencyModal(record)}
                style={{
                  borderColor: COLORS.secondary,
                  color: COLORS.secondary
                }}
              >
                Set Frequency
              </Button>
            )}
            
            <Popconfirm 
              title="Delete Form"
              description={`Are you sure you want to delete "${record.form_name}"?`}
              onConfirm={() => deleteForm(record.id)}
              okText="Yes"
              cancelText="No"
              okButtonProps={{ 
                style: { 
                  backgroundColor: COLORS.error,
                  borderColor: COLORS.error 
                } 
              }}
            >
              <Button 
                danger 
                icon={<DeleteOutlined />} 
                loading={deletingId === record.id}
                size="small"
                style={{ 
                  borderColor: COLORS.error,
                  color: COLORS.error
                }}
              />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Navbar onMenuClick={() => {}} />
      
      {/* MAIN CONTENT */}
      <div style={{
        paddingTop: 90,
        paddingLeft: 24,
        paddingRight: 24,
        paddingBottom: 24,
        minHeight: "100vh",
        backgroundColor: COLORS.lightBackground,
        marginLeft: 0,
        width: "100%",
        boxSizing: "border-box"
      }}>
        <Card 
          style={{ 
            borderRadius: 12,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            padding: 0,
            overflow: "hidden"
          }}
        >
          {/* Header */}
          <div style={{ 
            background: `linear-gradient(45deg, ${COLORS.primary}, ${COLORS.secondary})`,
            padding: "24px 30px",
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12
          }}>
            <Row justify="space-between" align="middle" gutter={[16, 16]}>
              <Col>
                <Title level={3} style={{ margin: 0, color: COLORS.white }}>
                  <FileTextOutlined style={{ marginRight: 12 }} />
                  Form Templates
                </Title>
                <div style={{ color: "rgba(255,255,255,0.8)", marginTop: 8 }}>
                  {rows.length} forms available
                </div>
              </Col>
              <Col>
                <Space size="middle" align="center" wrap>
                  <Search
                    placeholder="Search forms..."
                    prefix={<SearchOutlined style={{ color: COLORS.textSecondary }} />}
                    onSearch={handleSearch}
                    onChange={(e) => handleSearch(e.target.value)}
                    style={{ width: 280 }}
                    value={searchText}
                  />
                  <Space size="middle">
                    <Button 
                      icon={<ReloadOutlined />} 
                      onClick={fetchList} 
                      loading={loading}
                      style={{ 
                        background: "rgba(255,255,255,0.2)",
                        color: COLORS.white,
                        borderColor: "rgba(255,255,255,0.3)"
                      }}
                    >
                      Refresh
                    </Button>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={handleCreateNewForm}
                      style={{ 
                        background: COLORS.white,
                        color: COLORS.primary,
                        border: "none",
                        fontWeight: "bold"
                      }}
                    >
                      New Form
                    </Button>
                  </Space>
                </Space>
              </Col>
            </Row>
          </div>

          {/* Table */}
          <div style={{ 
            padding: "24px 30px 30px 30px",
            background: COLORS.white
          }}>
            <Table
              rowKey="id"
              columns={columns}
              dataSource={filteredRows}
              loading={loading}
              onChange={handleTableChange}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: rows.length,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} forms`,
                pageSizeOptions: ['10', '20', '50', '100'],
                position: ['bottomCenter'],
                style: { marginTop: 20 },
                simple: false,
                showTitle: true
              }}
              size="middle"
              style={{ 
                border: `1px solid ${COLORS.border}`,
                borderRadius: 8
              }}
              scroll={{ x: 1200 }}
            />
          </div>
        </Card>
      </div>

      {/* ========== ACCESS CONTROL MODAL ========== */}
      <Modal
        open={showAccessModal}
        onCancel={() => {
          setShowAccessModal(false);
          setSelectedFormForAccess(null);
        }}
        width={1000}
        title={
          <Space>
            <SafetyOutlined style={{ color: COLORS.primary }} />
            <span style={{ fontSize: '18px', fontWeight: 'bold' }}>
              Form Access Control - {selectedFormForAccess?.form_name}
            </span>
          </Space>
        }
        footer={null}
        centered
        maskClosable={true}
        destroyOnClose
        bodyStyle={{ padding: "0px", maxHeight: "calc(100vh - 150px)", overflowY: "auto" }}
      >
        {selectedFormForAccess && (
          <FormAccessControl
            language="english"
            permissions={accessPermissions}
            onPermissionsChange={handleAccessPermissionsChange}
            formId={selectedFormForAccess.id}
            onFormSave={() => {}}
          />
        )}
      </Modal>

      {/* ========== ACCESS REPORT MODAL ========== */}
      <AccessReportModal
        visible={showReportModal}
        onClose={() => { setShowReportModal(false); setSelectedFormForReport(null); }}
        formId={selectedFormForReport?.id}
        formName={selectedFormForReport?.form_name}
        language="english"
        onSaveSuccess={() => fetchList()}
      />

      {/* Create Form Modal - Same as before */}
      <Modal
        open={showCreateModal}
        onCancel={() => setShowCreateModal(false)}
        footer={null}
        width={1000}
        title={
          <Space>
            <FileAddOutlined style={{ color: COLORS.primary }} />
            <span style={{ fontSize: '18px', fontWeight: 'bold' }}>Create New Form</span>
          </Space>
        }
        maskClosable={true}
        centered
      >
        <div style={{ padding: "20px" }}>
          <Row gutter={24}>
            <Col xs={24} sm={12}>
              <Card
                hoverable
                style={{
                  borderRadius: '12px',
                  border: '2px solid #e8e8e8',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  height: '200px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onClick={handleCreateBlankForm}
                bodyStyle={{ padding: '24px', textAlign: 'center' }}
              >
                <div style={{ fontSize: '48px', color: COLORS.primary, marginBottom: '16px' }}>
                  <FormOutlined />
                </div>
                <Title level={4} style={{ margin: 0, color: COLORS.primary }}>
                  Blank Form
                </Title>
                <Button 
                  type="primary" 
                  style={{ marginTop: '20px', background: COLORS.primary }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCreateBlankForm();
                  }}
                >
                  Create Blank Form
                </Button>
              </Card>
            </Col>

            <Col xs={24} sm={12}>
              <Card
                hoverable
                style={{
                  borderRadius: '12px',
                  border: '2px solid #e8e8e8',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  height: '200px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                bodyStyle={{ padding: '24px', textAlign: 'center' }}
              >
                <div style={{ fontSize: '48px', color: COLORS.primary, marginBottom: '16px' }}>
                  <CopyFilled />
                </div>
                <Title level={4} style={{ margin: 0, color: COLORS.primary }}>
                  From Existing Form Template
                </Title>
                <Button 
                  style={{ 
                    marginTop: '20px', 
                    background: COLORS.primary,
                    borderColor: COLORS.primary,
                    color: 'white'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowCreateModal(false);
                    setShowFieldSelectionModal(true);
                  }}
                >
                  Select Form Template
                </Button>
              </Card>
            </Col>
          </Row>
        </div>
      </Modal>

      {/* Field Selection Modal - Same as before */}
      <Modal
        open={showFieldSelectionModal}
        onCancel={() => {
          setShowFieldSelectionModal(false);
          setSelectedSourceForm(null);
          setSourceFormSchema([]);
          setSelectedFields([]);
        }}
        footer={null}
        width={800}
        title={
          <Space>
            <CopyFilled style={{ color: COLORS.primary }} />
            <span style={{ fontSize: '18px', fontWeight: 'bold' }}>Create Form from Existing Template</span>
          </Space>
        }
        maskClosable={false}
        centered
      >
        <div style={{ padding: "20px" }}>
          <div style={{ marginBottom: 24 }}>
            <Title level={5}>Step 1: Select Source Form</Title>
            <Select
              style={{ width: '100%' }}
              placeholder="Select a form to copy fields from"
              onChange={(value) => loadSourceFormSchema(value)}
              loading={loadingForms}
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {availableForms.map(form => (
                <Option key={form.id} value={form.id}>
                  {form.form_name} (ID: {form.id})
                </Option>
              ))}
            </Select>
          </div>

          {sourceFormSchema.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Title level={5}>Step 2: Select Fields to Include</Title>
                <Space>
                  <Button size="small" onClick={() => {
                    const allIds = [];
                    const collectIds = (nodes) => {
                      nodes.forEach(node => {
                        allIds.push(node.id);
                        if (node.children) {
                          if (Array.isArray(node.children)) {
                            collectIds(node.children);
                          } else {
                            if (node.children.left) collectIds(node.children.left);
                            if (node.children.right) collectIds(node.children.right);
                          }
                        }
                        if (node.subFields) {
                          collectIds(node.subFields);
                        }
                      });
                    };
                    collectIds(sourceFormSchema);
                    setSelectedFields(allIds);
                  }}>Select All</Button>
                  <Button size="small" onClick={() => setSelectedFields([])}>Clear All</Button>
                </Space>
              </div>

              <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: '8px', padding: '16px' }}>
                {loadingForms ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}><Spin /></div>
                ) : (
                  <Tree
                    checkable
                    expandedKeys={expandedKeys}
                    onExpand={setExpandedKeys}
                    checkedKeys={selectedFields}
                    onCheck={(checked) => setSelectedFields(checked)}
                    treeData={[{
                      title: <Text strong>{selectedSourceForm?.name}</Text>,
                      key: 'root',
                      children: sourceFormSchema.map(field => ({
                        key: field.id,
                        title: (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '16px' }}>
                              {field.type === 'text' && <FontSizeOutlined />}
                              {field.type === 'number' && <NumberOutlined />}
                              {field.type === 'date' && <CalendarOutlined />}
                              {field.type === 'checkbox' && <CheckSquareOutlined />}
                              {field.type === 'radio' && <BorderOutlined />}
                              {field.type === 'heading' && <FontSizeOutlined />}
                              {field.type === 'paragraph' && <AlignLeftOutlined />}
                              {field.type === 'comparison' && <SwapOutlined />}
                              {field.type === 'graph' && <BarChartOutlined />}
                            </span>
                            <Text strong>{field.enLabel || field.label || field.type}</Text>
                            {field.required && <Tag color="red">Required</Tag>}
                            <Tag color="blue">{field.type}</Tag>
                          </div>
                        ),
                        children: field.children ? (
                          Array.isArray(field.children) ? field.children.map(child => ({
                            key: child.id,
                            title: (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 24 }}>
                                <span style={{ fontSize: '14px', color: '#999' }}>↳</span>
                                <Text>{child.enLabel || child.label || child.type}</Text>
                                <Tag color="purple">subfield</Tag>
                              </div>
                            )
                          })) : [
                            ...(field.children.left || []).map(child => ({
                              key: child.id,
                              title: (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 24 }}>
                                  <span style={{ fontSize: '14px', color: '#52c41a' }}>←</span>
                                  <Text>{child.enLabel || child.label || child.type}</Text>
                                  <Tag color="green">left</Tag>
                                </div>
                              )
                            })),
                            ...(field.children.right || []).map(child => ({
                              key: child.id,
                              title: (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 24 }}>
                                  <span style={{ fontSize: '14px', color: '#1890ff' }}>→</span>
                                  <Text>{child.enLabel || child.label || child.type}</Text>
                                  <Tag color="blue">right</Tag>
                                </div>
                              )
                            }))
                          ]
                        ) : undefined,
                        children: field.subFields ? field.subFields.map(sub => ({
                          key: sub.id,
                          title: (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 24 }}>
                              <CalculatorOutlined style={{ color: '#1890ff' }} />
                              <Text>{sub.enLabel || sub.label || 'Subfield'}</Text>
                              <Tag color="orange">calculation</Tag>
                            </div>
                          )
                        })) : undefined
                      }))
                    }]}
                  />
                )}
              </div>
              <div style={{ marginTop: 16 }}>
                <Alert message={`${selectedFields.length} fields selected`} type="info" />
              </div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: 24 }}>
            <Button onClick={() => setShowFieldSelectionModal(false)}>Cancel</Button>
            <Button type="primary" icon={<CopyFilled />} onClick={createFormFromExisting} loading={creatingFromExisting} disabled={!selectedSourceForm || selectedFields.length === 0}>Create Form</Button>
          </div>
        </div>
      </Modal>

      {/* Edit Approved Form Confirmation Modal */}
      <Modal
        open={showEditConfirmModal}
        onCancel={() => {
          setShowEditConfirmModal(false);
          setFormToEdit(null);
        }}
        footer={null}
        width={500}
        centered
      >
        <div style={{ padding: "24px", textAlign: "center" }}>
          <WarningOutlined style={{ fontSize: "48px", color: COLORS.warning, marginBottom: "16px" }} />
          <Title level={4} style={{ color: COLORS.warning }}>Edit Approved Form</Title>
          <Text style={{ display: "block", marginBottom: "24px" }}>
            This form has been approved. Editing it will change its status to "Pending" and it will need to be reviewed and approved again by the approver.
          </Text>
          <Text type="secondary" style={{ display: "block", marginBottom: "24px", fontSize: "12px" }}>
            Form: <strong>{formToEdit?.form_name}</strong>
          </Text>
          <Space>
            <Button onClick={() => setShowEditConfirmModal(false)} size="large">Cancel</Button>
            <Button type="primary" danger onClick={handleConfirmEditApproved} size="large" style={{ backgroundColor: COLORS.warning, borderColor: COLORS.warning }}>Yes, Edit Form</Button>
          </Space>
        </div>
      </Modal>

      {/* Frequency Settings Modal */}
      <Modal
        open={showFrequencyModal}
        onCancel={() => setShowFrequencyModal(false)}
        footer={null}
        width={550}
        title={<Space><FieldTimeOutlined />Set Frequency for "{selectedFormForFrequency?.form_name || 'Form'}"</Space>}
        centered
      >
        <div style={{ padding: "20px" }}>
          <div style={{ marginBottom: 20 }}>
            <Text strong>Frequency Name:</Text>
            <Input placeholder="e.g., Monthly Report, Quarterly Review" value={frequencyName} onChange={(e) => setFrequencyName(e.target.value)} size="large" />
          </div>
          <div style={{ marginBottom: 20 }}>
            <Text strong>Select Date:</Text>
            <DatePicker style={{ width: '100%' }} onChange={(date) => setFrequencyDate(date)} format={frequencyFormat} size="large" />
          </div>
          <div style={{ marginBottom: 20 }}>
            <Text strong>Select Time:</Text>
            <TimePicker style={{ width: '100%' }} onChange={(time) => setFrequencyTime(time)} format="HH:mm" size="large" defaultValue={dayjs("09:00", "HH:mm")} />
          </div>
          <div style={{ marginBottom: 20 }}>
            <Text strong>Date Format:</Text>
            <Select style={{ width: '100%' }} value={frequencyFormat} onChange={(value) => setFrequencyFormat(value)} size="large">
              {DATE_FORMATS.map(format => (<Option key={format.value} value={format.value}>{format.label}</Option>))}
            </Select>
          </div>
          {frequencyDate && (
            <div style={{ padding: 12, background: "#e6f7ff", borderRadius: 8 }}>
              <Space><CheckCircleOutlined />Preview: {frequencyName} on {frequencyDate.format(frequencyFormat)} at {frequencyTime?.format("HH:mm")}</Space>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: 24 }}>
            <Button onClick={() => setShowFrequencyModal(false)}>Cancel</Button>
            <Button type="primary" icon={<CheckCircleOutlined />} onClick={saveFrequencySettings} loading={savingFrequency} style={{ background: COLORS.success }}>Save Frequency</Button>
          </div>
        </div>
      </Modal>
    </>
  );
}